import React, {
  useState,
  useEffect,
  ReactNode,
  MouseEvent,
  useMemo,
  PropsWithChildren,
  useLayoutEffect,
  useRef,
} from 'react';
import { BoxProps, Paper, PaperProps, iconButtonClasses } from '@mui/material';
import {
  GridColDef,
  DataGridPro,
  gridClasses,
  GridRowsProp,
  GridSortModel,
  GridToolbarExport,
  GridPaginationModel,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarQuickFilterProps,
  GridValidRowModel,
  GridCellParams,
  DataGridProProps,
  GridTreeNodeWithRender,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import merge from 'lodash/merge';
import * as ReactRouter from 'react-router-dom';

type SxOverrides = {
  /** sx style overrides for the container `div`, when `noWrap` is `true`. */
  box: BoxProps['sx'];
  /** sx style overrides for the container `Paper`, when `noWrap` is `false`. */
  wrap: PaperProps['sx'];
  /** sx style overrides for `DataGridPro`. */
  grid: DataGridProProps['sx'];
  /**
   * A shorthand style setting for `padding-left` of the leftmost cell
   * and `padding-right` of the rightmost cell. It is also applied to
   * the toolbar, if any. Default is `2`
   */
  sidePadding?: number;
};

type DocumentsListProps<R extends GridValidRowModel> = Pick<DataGridProProps<R>, 'getRowId'> & {
  rows: GridRowsProp<R>;
  columns: GridColDef<R>[];
  isLoading?: boolean;
  onCellClick?: (params: GridCellParams<R>, event: MouseEvent) => void;
  getRowLinkHref?: (
    params: GridRenderCellParams<R, any, any, GridTreeNodeWithRender>
  ) => ReactRouter.To | null | undefined;
  storageKey?: string;
  allowExport?: boolean;
  rowHeight?: number;
  showFilter?: boolean;
  hideFooter?: boolean;
  filter?: ReactNode;
  noWrap?: boolean;
  hidePageSizeOptions?: boolean;
  disableVirtualization?: boolean;
  initialSortModel?: GridSortModel;
  sx?: Partial<SxOverrides>;
};

export const docListClasses = {
  linkCell: 'docList--link-cell',
  noPaddingCell: 'docList--no-padding-cell',
  cellLink: 'docList--cell-link',
  cellLinkContent: 'docList--cell-link-content',
  noLinkCell: 'docList--no-link-cell',
} as const;

function CellLink({ to, children }: PropsWithChildren<{ to: ReactRouter.To }>) {
  return (
    <ReactRouter.Link to={to} className={docListClasses.cellLink} tabIndex={-1}>
      <div className={docListClasses.cellLinkContent}>{children}</div>
    </ReactRouter.Link>
  );
}

const defaultRowHeight = 45;
const minBoxHeight = 160;
const maxBoxHeight = 600;
const defaultPageSizeOptions = [5, 10, 25, 50];

const defaultPaginationState: GridPaginationModel = {
  pageSize: 25,
  page: 0,
};

const defaultSortState: GridSortModel = [
  {
    field: 'updatedAt',
    sort: 'desc',
  },
];

const getDefaultSortModel = (storageKey?: string, initial: GridSortModel = defaultSortState): GridSortModel => {
  if (!storageKey) {
    return initial;
  }

  return JSON.parse(sessionStorage.getItem(`${storageKey}.sort`) ?? JSON.stringify(initial));
};

const getDefaultPaginationModel = (
  storageKey?: string,
  initial: GridPaginationModel = defaultPaginationState
): GridPaginationModel => {
  if (!storageKey) {
    return initial;
  }

  return JSON.parse(sessionStorage.getItem(`${storageKey}.pagination`) ?? JSON.stringify(initial));
};

export const DocumentsList = <R extends GridValidRowModel = GridValidRowModel>({
  rows,
  rowHeight = defaultRowHeight,
  filter,
  columns,
  storageKey,
  onCellClick,
  getRowId,
  getRowLinkHref,
  isLoading = false,
  showFilter = true,
  hideFooter,
  allowExport = false,
  noWrap = false,
  hidePageSizeOptions = false,
  disableVirtualization = false,
  initialSortModel = defaultSortState,
  sx,
}: DocumentsListProps<R>) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    getDefaultPaginationModel(storageKey, defaultPaginationState)
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(getDefaultSortModel(storageKey, initialSortModel));
  const boxRef = useRef<HTMLDivElement>(null);
  const apiRef = useGridApiRef();
  const mappedColumns = useMemo(() => {
    if (!getRowLinkHref) return columns;

    return columns.map((col): GridColDef<R> => {
      const { cellClassName: cellClassNameOrig } = col;
      let suppressLink = false;

      const addClasses = (className?: string) => {
        suppressLink = className?.includes(docListClasses.noLinkCell) ?? false;
        return suppressLink
          ? className!
          : `${className ?? ''} ${docListClasses.linkCell} ${docListClasses.noPaddingCell}`;
      };

      const cellClassName: GridColDef<R>['cellClassName'] =
        typeof cellClassNameOrig === 'function'
          ? (params) => addClasses(cellClassNameOrig.call(col, params))
          : addClasses(cellClassNameOrig);

      return {
        ...col,
        cellClassName,
        renderCell: (params) => {
          const content = col.renderCell?.(params) ?? params.formattedValue;
          const to = !suppressLink && getRowLinkHref(params);
          return to ? <CellLink to={to}>{content}</CellLink> : content;
        },
      };
    });
  }, [columns, getRowLinkHref]);

  const sidePadding = sx?.sidePadding ?? 2;
  const rowCount = rows?.length ?? 0;
  const loRowIdx = paginationModel.page * paginationModel.pageSize;
  const pageRowCount = Math.min(loRowIdx + paginationModel.pageSize, rowCount) - loRowIdx;
  const isPagingPresent = rowCount > paginationModel.pageSize;

  useEffect(() => {
    if (storageKey) {
      sessionStorage.setItem(`${storageKey}.sort`, JSON.stringify(sortModel));
      sessionStorage.setItem(`${storageKey}.pagination`, JSON.stringify(paginationModel));
    }
  }, [storageKey, paginationModel, sortModel]);

  useLayoutEffect(() => {
    const api = apiRef.current;
    const box = boxRef.current;
    if (!noWrap || !api || !box) return;

    // In noWrap mode, the grid's container should have intrinsic height.
    // Therefore, we calculate the height of the grid, an if it exceeds the
    // max height, the vertical scrolling is turned on.

    const { topContainerHeight } = api.getRootDimensions();
    let totalHeight = minBoxHeight;
    if (pageRowCount > 0) {
      const rowsHeight = (isPagingPresent ? paginationModel.pageSize : pageRowCount) * rowHeight;
      const footerHeight = isPagingPresent
        ? // on first render, fallback to the default row height
          (api.rootElementRef.current?.querySelector(`.${gridClasses.footerContainer}`)?.getBoundingClientRect()
            .height ?? rowHeight)
        : 0;
      totalHeight = rowsHeight + topContainerHeight + footerHeight + 3;
    }
    const boxHeight = `${Math.min(totalHeight, maxBoxHeight)}px`;
    box.style.height = boxHeight;
  }, [apiRef, isPagingPresent, noWrap, pageRowCount, paginationModel.pageSize, rowHeight]);

  const quickFilterParser = (input: string) => {
    const values = input.split(',').map((value) => value.trim());

    if (storageKey) {
      sessionStorage.setItem(`${storageKey}.filter`, JSON.stringify(values));
    }

    return values;
  };

  const quickFilterProps: GridToolbarQuickFilterProps = {
    sx: { pb: 0 },
    debounceMs: 500,
    quickFilterParser,
  };

  const grid = (
    <DataGridPro
      pagination
      rows={rows}
      rowHeight={rowHeight}
      columns={mappedColumns}
      loading={isLoading}
      disableColumnFilter
      disableColumnSelector
      disableDensitySelector
      disableRowSelectionOnClick
      disableVirtualization={disableVirtualization}
      apiRef={apiRef}
      sortModel={sortModel}
      hideFooter={isPagingPresent ? false : (hideFooter ?? true)}
      onCellClick={onCellClick}
      pageSizeOptions={hidePageSizeOptions ? [defaultPaginationState.pageSize] : defaultPageSizeOptions}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
      getRowId={getRowId}
      getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
      initialState={{
        filter: {
          filterModel: {
            items: [],
            quickFilterValues: JSON.parse(localStorage.getItem(`${storageKey}.filter`) ?? JSON.stringify([])),
          },
        },
        sorting: {
          sortModel: initialSortModel,
        },
      }}
      slots={
        showFilter
          ? {
              toolbar: () => (
                <GridToolbarContainer sx={{ py: 4, px: sidePadding }}>
                  {allowExport && <GridToolbarExport />}
                  {filter && filter}

                  <GridToolbarQuickFilter {...quickFilterProps} sx={{ ml: 'auto', pb: 0 }} />
                </GridToolbarContainer>
              ),
            }
          : {}
      }
      sx={merge(
        {
          ...(noWrap && {
            borderRadius: 0,
            borderWidth: 0,
            borderBottom: 1,
            borderBottomColor: 'secondary.light',
            // when no toolbar present, topmost corner rounding should be reset
            [`&.${gridClasses['root--noToolbar']} [aria-rowindex="1"]`]: {
              '& [aria-colindex="1"]': { borderTopLeftRadius: 0 },
              [`& .${gridClasses['columnHeader--last']}`]: { borderTopRightRadius: 0 },
            },
          }),

          [`& .${iconButtonClasses.root}`]: {
            padding: '5px',
          },

          [`& .${gridClasses.cell}`]: {
            [`&.${docListClasses.noPaddingCell}`]: { padding: 0 },
            [`&[aria-colindex="1"]:not(.${docListClasses.noPaddingCell})`]: {
              paddingLeft: sidePadding,
            },
            '&:hover': {
              cursor: 'pointer',
              textWrap: 'auto',
              wordWrap: 'break-word',
            },
            '&:focus-within': {
              outline: 'none',
            },
            [`&:has(.${iconButtonClasses.root})`]: {
              textOverflow: 'clip',
            },
            ...(getRowLinkHref && {
              [`&.${docListClasses.linkCell}`]: {
                display: 'flex',
                alignItems: 'center',
                [`& .${docListClasses.cellLink}`]: {
                  flexGrow: 1,
                  minWidth: 0,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: 1,
                  paddingRight: 1,
                  lineHeight: 'normal',
                  color: 'inherit',
                  textDecoration: 'none',
                  outline: 'none',
                  [`& .${docListClasses.cellLinkContent}`]: {
                    display: 'block',
                    flexGrow: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                },
              },
              [`&[aria-colindex="1"] .${docListClasses.cellLink}`]: {
                paddingLeft: sidePadding,
              },
            }),
          },

          [`& .${gridClasses.columnHeader}`]: {
            borderBottom: 1,
            borderBottomColor: 'secondary.light',
            ...(noWrap && {
              borderTop: 1,
              borderTopColor: 'secondary.light',
            }),
            ...(showFilter && {
              borderTop: 1,
              borderTopColor: 'secondary.light',
            }),
            [`&[aria-colindex="1"]`]: {
              paddingLeft: sidePadding,
            },
          },

          [`& .${gridClasses.columnHeaderTitle}`]: {
            fontWeight: 700,
          },

          [`& .${gridClasses.row}`]: {
            outline: 'none',
            '&.even': {
              backgroundColor: 'transparent',
            },
            '&.odd': {
              backgroundColor: '#FAFAFA',
            },
            '&:hover, &.Mui-hovered': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              '@media (hover: none)': {
                backgroundColor: 'transparent',
              },
              ...(getRowLinkHref && {
                [`& .${gridClasses.cell} .${docListClasses.cellLink}`]: {
                  '&, &:hover': { color: 'primary.main' },
                },
              }),
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(159, 57, 118, 0.08)',
              '&:hover, &.Mui-hovered': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            },
          },
        },
        sx?.grid
      )}
    />
  );

  return noWrap ? <div ref={boxRef}>{grid}</div> : <Paper sx={{ width: '100%' }}>{grid}</Paper>;
};
